import type { PatchTournamentRequest, Tournament } from '@brush/api-client';
import { useState } from 'react';
import { btnGhost, btnPrimary, Modal, modalInput } from '../Modal';
import { changedFrom } from '../../dialogDrafts';
import { useUnsavedWork } from '../../unsaved';
import { TagInput } from '../TagInput';
import {
  draftFrom,
  draftIsValid,
  TournamentFields,
  type TournamentDraft,
} from './TournamentFields';

export function EditTournamentModal({
  tournament,
  allTags,
  platformTags,
  saving,
  deleting,
  onSave,
  onDelete,
  onClose,
}: {
  tournament: Tournament;
  allTags: string[];
  platformTags: string[];
  saving: boolean;
  deleting: boolean;
  /**
   * Emits the patch itself rather than the draft. Only this component knows that an empty
   * guarantee box means "erase it" rather than "leave it alone", and that distinction has to
   * survive the trip to the server.
   */
  onSave: (patch: Partial<PatchTournamentRequest>) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [opened] = useState<TournamentDraft>(() => draftFrom(tournament));
  const [draft, setDraft] = useState<TournamentDraft>(opened);
  // Typed changes are asked about before a room switch or any other way out discards them (#591).
  useUnsavedWork(changedFrom(opened, draft));
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [typedName, setTypedName] = useState('');

  /*
   * Name, date and buy-in are settled once players have bought in against them, so they stop
   * being editable when the tournament leaves `scheduled`. Tags never lock: retagging a
   * finished tournament is how it gets picked up by a leaderboard or rating.
   */
  const canEditSchedule = tournament.status === 'scheduled';

  const valid = !canEditSchedule || draftIsValid(draft);

  const save = () =>
    onSave(
      canEditSchedule
        ? {
            internalId: draft.internalId.trim(),
            name: draft.name.trim(),
            date: draft.date,
            time: draft.time,
            buyIn: Number(draft.buyIn),
            // Empty string is the erase signal for the two optional strings; the decimal needs
            // its own flag, because null on a PATCH already means "leave unchanged".
            guarantee: draft.guarantee,
            clearGuarantee: draft.guarantee === null,
            guaranteeUnits: draft.guaranteeUnits,
            eventNumber: draft.eventNumber,
            isMultiDay: draft.isMultiDay,
            sessions: draft.sessions,
            tags: draft.tags,
          }
        : { tags: draft.tags },
    );

  /*
   * Typing the name is the confirmation. It matches the *saved* name, not the draft in the form
   * above — an unsaved rename would otherwise change the phrase you have to type to delete the
   * record as it exists on the server. Case and surrounding space are forgiven: this guards
   * against a mis-click, not against someone who means it.
   */
  const nameMatches = typedName.trim().toLowerCase() === tournament.name.trim().toLowerCase();

  return (
    <Modal
      title="Edit tournament"
      subtitle={
        canEditSchedule
          ? 'Results are uploaded separately.'
          : `This tournament is ${tournament.status} — only its tags can still be changed.`
      }
      onClose={onClose}
      width="max-w-2xl"
      footer={
        <>
          {/* A tournament with results behind it is history, so deleting stops at `scheduled`
              for the same reason the date does. */}
          {canEditSchedule ? (
            <button
              type="button"
              onClick={() => (confirmingDelete ? onDelete() : setConfirmingDelete(true))}
              disabled={deleting || (confirmingDelete && !nameMatches)}
              className="rounded-lg border border-chip-500/40 px-4 py-2 text-sm text-chip-500 hover:bg-chip-500/5 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : confirmingDelete ? 'Confirm delete' : 'Delete'}
            </button>
          ) : null}
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!valid || saving || deleting}
            className={btnPrimary}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </>
      }
    >
      {canEditSchedule ? (
        <TournamentFields
          draft={draft}
          onChange={setDraft}
          allTags={allTags}
          platformTags={platformTags}
          sessionsRemovedWarning={draft.isMultiDay ? undefined : tournament.sessions.length}
        />
      ) : (
        // Locked: the whole form would be read-only theatre, so only what can still change is
        // shown. The subtitle above says why.
        <div className="min-w-0">
          <span className="mb-1 block text-xs text-ink-800/60">Tags</span>
          <TagInput
            value={draft.tags}
            onChange={(tags) => setDraft({ ...draft, tags })}
            suggestions={allTags}
            platformTags={platformTags}
          />
          <p className="mt-1 text-[10px] text-ink-800/40">
            Platform tags carry a shield and can&apos;t be removed by rooms once applied.
          </p>
        </div>
      )}

      {confirmingDelete ? (
        <div className="mt-6 rounded-lg border border-chip-500/40 bg-chip-500/5 p-4">
          <p className="text-xs text-ink-800/70">
            Deleting removes this tournament permanently, along with any leaderboard entry that
            includes it. To confirm, type{' '}
            <span className="font-medium text-ink-950">{tournament.name}</span> below.
          </p>
          <input
            autoFocus
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            aria-label={`Type ${tournament.name} to confirm deletion`}
            className={`${modalInput} mt-2`}
          />
          <button
            type="button"
            onClick={() => {
              setConfirmingDelete(false);
              setTypedName('');
            }}
            className="mt-2 text-[11px] text-ink-800/60 underline underline-offset-2 hover:text-ink-950"
          >
            Never mind, keep this tournament
          </button>
        </div>
      ) : null}
    </Modal>
  );
}
